import { useEffect, useMemo, useRef, useState } from "react";

import {
  fetchOncoEmrUserDirectory,
  removeTaskRecipient,
  retryTaskWriteback,
} from "../../../api/tasks/tasksApi";
import { TaskRecipient } from "../../../api/tasks/taskTypes";

interface RecipientChipsProps {
  taskRowId: string;
  orgId: string;
  recipients?: TaskRecipient[];
  /**
   * OncoEMR id of the ingest inbox recipient. Its ✕ is suppressed: removing it
   * would drop the task out of the polled inbox, and the backend hard-rejects it
   * with a 422 (no writeback row), so a Retry would 404 forever.
   */
  inboxRecipientUserId?: string | null;
}

type RemoveState = "idle" | "removing" | "failed";

/** Per-chip removal bookkeeping: current state + the key to retry the same send. */
interface ChipState {
  state: RemoveState;
  retryKey: string | null;
}

/**
 * Resolve a recipient's display name: prefer the payload name, then the cached
 * OncoEMR directory (id -> name), finally fall back to the raw UserId.
 */
function resolveName(
  recipient: TaskRecipient,
  directory: Map<string, string>,
): string {
  if (recipient.name && recipient.name.trim() !== "") return recipient.name;
  return directory.get(recipient.id) ?? recipient.id;
}

/**
 * Recipient chips with a remove (✕) control. Renders the task's current
 * recipients, resolving names from the cached OncoEMR directory, and removes one
 * via the writeback API with optimistic hide + Retry on failure. Mount with
 * `key={taskRowId}` so it resets per task.
 */
const RecipientChips = ({
  taskRowId,
  orgId,
  recipients,
  inboxRecipientUserId,
}: RecipientChipsProps) => {
  const [directory, setDirectory] = useState<Map<string, string>>(new Map());
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [chipStates, setChipStates] = useState<Record<string, ChipState>>({});
  const removeKeys = useRef<Record<string, string>>({});

  useEffect(() => {
    let active = true;
    fetchOncoEmrUserDirectory(orgId)
      .then((users) => {
        if (!active) return;
        const map = new Map<string, string>();
        users.forEach((user) => {
          if (user.first_and_last)
            map.set(user.oncoemr_user_id, user.first_and_last);
        });
        setDirectory(map);
      })
      .catch(() => {
        // Directory is a convenience layer only — chips fall back to the raw UserId.
      });
    return () => {
      active = false;
    };
  }, [orgId]);

  const visible = useMemo(
    // A backend that predates the `recipients` field returns it undefined.
    () => (recipients ?? []).filter((recipient) => !removed.has(recipient.id)),
    [recipients, removed],
  );

  const runRemove = async (
    recipientId: string,
    send: () => ReturnType<typeof removeTaskRecipient>,
  ) => {
    setChipStates((prev) => ({
      ...prev,
      [recipientId]: {
        state: "removing",
        retryKey: removeKeys.current[recipientId],
      },
    }));
    try {
      const result = await send();
      if (result.status === "sent") {
        setRemoved((prev) => new Set(prev).add(recipientId));
      } else {
        setChipStates((prev) => ({
          ...prev,
          [recipientId]: {
            state: "failed",
            retryKey: removeKeys.current[recipientId],
          },
        }));
      }
    } catch {
      setChipStates((prev) => ({
        ...prev,
        [recipientId]: {
          state: "failed",
          retryKey: removeKeys.current[recipientId],
        },
      }));
    }
  };

  const handleRemove = (recipientId: string) => {
    if (chipStates[recipientId]?.state === "removing") return;
    const key = crypto.randomUUID();
    removeKeys.current[recipientId] = key;
    void runRemove(recipientId, () =>
      removeTaskRecipient(taskRowId, recipientId, key),
    );
  };

  const handleRetry = (recipientId: string) => {
    const key = removeKeys.current[recipientId];
    if (!key) return;
    void runRemove(recipientId, () => retryTaskWriteback(key));
  };

  if (visible.length === 0) return null;

  return (
    <div className="mt-5 flex flex-col gap-2 border-t border-primaryGray-15 pt-4">
      <span className="text-tiny text-primaryGray-7">Recipients</span>
      <div className="flex flex-wrap gap-2">
        {visible.map((recipient) => {
          const chip = chipStates[recipient.id];
          const removing = chip?.state === "removing";
          const failed = chip?.state === "failed";
          // The inbox recipient can't be removed (see prop doc) — render its chip
          // with no ✕ so the unremovable state is unreachable from the UI.
          const isInbox =
            inboxRecipientUserId != null &&
            recipient.id === inboxRecipientUserId;
          return (
            <span
              key={recipient.id}
              className="flex items-center gap-1.5 rounded-full border border-primaryGray-14 bg-primaryGray-16 py-1 pl-3 pr-2 text-small text-primaryGray-2"
            >
              {resolveName(recipient, directory)}
              {!isInbox &&
                (failed ? (
                  <button
                    type="button"
                    className="text-tiny font-semiBold text-tertiaryRed-3 hover:underline"
                    onClick={() => handleRetry(recipient.id)}
                  >
                    Retry
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={removing}
                    aria-label={`Remove ${resolveName(recipient, directory)}`}
                    className="flex h-4 w-4 items-center justify-center rounded-full text-primaryGray-7 hover:bg-primaryGray-14 hover:text-primaryGray-2 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() => handleRemove(recipient.id)}
                  >
                    {removing ? "…" : "✕"}
                  </button>
                ))}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default RecipientChips;
